import { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import FlightFooterRow from "./FlightFooterRow";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Collapse,
  ConfigProvider,
  Descriptions,
  Divider,
  Drawer,
  Select,
  Skeleton,
  Tabs,
  Badge,
  Space,
  Typography,
  App,
} from "antd";
import FlightsFooter from "./FlightsFooter";
import FlightSvg from "../../assets/images/filters/Flights.svg";
import { useSearch } from "../../contexts/SearchContext";
import "./Index.scss";
import { useSelectedResult } from "../../contexts/SelectedResultContext";
import { useAppContext } from "../../contexts/AppContext";
import {
  SwitcherOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { convertPrice } from "../../services/currencyService";
import { getUserDetails } from "../../services/authService";
import { FaPlane } from "react-icons/fa";
import PackageFlightLegUnified from "../Common/UnifiedPackageFlightLeg/PackageFlightLegUnified";
import "../FlightBookingFlowMockup/flightBookingMockup/FlightOfferResultCard.css";

const { Title, Text } = Typography;

/** שורות הלוך/חזור לתצוגת compact — מבנה אחד כמו כרטיס תוצאות טיסה */
export function flattenPackageCompactFlightRows(flightsToRender) {
  const rows = [];
  if (!flightsToRender) return rows;

  const pushLegs = (legs, directionLabel, directionKey) => {
    const list = (legs || []).filter(Boolean);
    list.forEach((leg, legIndex) => {
      rows.push({
        leg,
        labelHe: directionLabel,
        key: `${directionKey}-${legIndex}`,
      });
    });
  };

  if (Array.isArray(flightsToRender)) {
    flightsToRender.slice(0, 2).forEach((flight, directionIndex) => {
      let legs = [];
      if (flight && flight.leg) {
        legs = Array.isArray(flight.leg) ? flight.leg : [flight.leg];
      }
      const directionLabel =
        directionIndex === 0 ? "טיסה הלוך" : "טיסה חזור";
      pushLegs(legs, directionLabel, directionIndex);
    });
  } else if (flightsToRender.leg) {
    const legs = Array.isArray(flightsToRender.leg)
      ? flightsToRender.leg
      : [flightsToRender.leg];
    legs.filter(Boolean).forEach((leg, idx) => {
      rows.push({
        leg,
        labelHe: idx === 0 ? "טיסה הלוך" : "טיסה חזור",
        key: `s-${idx}`,
      });
    });
  }

  return rows.map((r, i) => ({ ...r, showDivider: i > 0, legIndex: i }));
}

export function packageProfileRouteHeading(firstLeg) {
  if (!firstLeg) return null;
  const o = firstLeg.depAirport?.code || firstLeg.depCity || "";
  const d = firstLeg.arrAirport?.code || firstLeg.arrCity || "";
  const oc = String(o).trim().toUpperCase();
  const dc = String(d).trim().toUpperCase();
  if (!oc && !dc) return null;
  return `${oc || "—"} ↔ ${dc || "—"}`;
}

const FlightSelection = ({
  linkedFlightsData = undefined,
  loadingLinkedFlights,
  isCard,
  isCollaped,
  isSelectable,
  title = undefined,
  inTourObject = false, // New prop to identify tour object context
  isCompact = false, // New prop for compact display in dynamic packages
  includeTitle = true, // New prop to control title display
  showOnlyButton = false, // New prop to show only the "view more flights" button
  forceShowButton = false, // Force render the button even without extra flights
  hideSelectionTrigger = false, // Hide "view more flights" trigger button
  customButtonText = undefined, // Custom text for the button (overrides default)
  savedDeal = undefined, // Allow passing savedDeal from parent
  setSavedDeal = undefined, // Allow passing setSavedDeal from parent
  selectedDepart = undefined, // Allow passing selectedDepart from parent
  setSelectedDepart = undefined, // Allow passing setSelectedDepart from parent
  selectedReturn = undefined, // Allow passing selectedReturn from parent
  setSelectedReturn = undefined, // Allow passing setSelectedReturn from parent
  onFlightSelect = undefined, // Callback when a flight is selected
}) => {
  const { isMobile, currencyRate } = useAppContext();
  const { initLdsSupplements } = useSelectedResult();
  const { notification } = App.useApp();

  const {
    linkedFlights: linkedFlightsOrigin,
    availableFlights,
    savedDeal: ogSavedDeal,
    setSavedDeal: ogSetSavedDeal,
    initSupplements,
    supplements,
    selectedDepart: ogSelectedDepart,
    selectedReturn: ogSelectedReturn,
    setSelectedDepart: ogSetSelectedDepart,
    setSelectedReturn: ogSetSelectedReturn,
    calculatFlightLDSPrice,
    calculateLDSDealFare,
    roomInfo,
    categories,
    flightTripType,
  } = useSearch();

  if (setSavedDeal === undefined) setSavedDeal = ogSetSavedDeal;
  if (savedDeal === undefined) savedDeal = ogSavedDeal;
  if (setSelectedDepart === undefined) setSelectedDepart = ogSetSelectedDepart;
  if (setSelectedReturn === undefined) setSelectedReturn = ogSetSelectedReturn;
  if (selectedDepart === undefined) selectedDepart = ogSelectedDepart;
  if (selectedReturn === undefined) selectedReturn = ogSelectedReturn;

  const linkedFlights = useMemo(() => {
    if (linkedFlightsData) return linkedFlightsData;
    return linkedFlightsOrigin;
  }, [linkedFlightsData, linkedFlightsOrigin]);

  const { t } = useTranslation();

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectedLinkedFlightIndex, setSelectedLinkedFlightIndex] = useState(0);
  const [sortBy, setSortBy] = useState("price"); // "price", "time", "duration"
  const passengersCountForPricing = useMemo(() => {
    const fromRooms = roomInfo?.rooms?.reduce(
      (sum, room) =>
        sum + Number(room?.adults || 0) + Number(room?.kids || 0) + Number(room?.infants || 0),
      0
    );
    const parsed = Number(fromRooms);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }, [roomInfo]);

  const buildFlightResultPriceDisplay = ({ rawPrice, price, deal }) => {
    const parsedRaw = Number(rawPrice);
    const safeRaw = Number.isFinite(parsedRaw) ? parsedRaw : 0;
    const passengersCount = passengersCountForPricing || 1;
    const calculatedPerPersonPrice = safeRaw / passengersCount;
    const origCurrency = String(
      price?.ourTotalFare?.originalCurrency || deal?.fareDetails?.currency || ""
    )
      .trim()
      .toUpperCase();
    const origCurrencyTotal = Number(
      price?.ourTotalFare?.origCurrency?.priceWithCommissionAndCreditCardFee
    );
    const hasFinalClientUsd =
      origCurrency === "USD" &&
      Number.isFinite(origCurrencyTotal) &&
      origCurrencyTotal > 0;
    const displayedUsd = hasFinalClientUsd ? origCurrencyTotal / passengersCount : null;
    const sourceFieldUsedForDisplay = "finalClientUsd";
    if (process.env.NODE_ENV === "development" && !hasFinalClientUsd) {
      console.warn("[FLIGHT_DISPLAY_PRICE_MISSING_FINAL_USD]", {
        dealId: String(deal?.dealId ?? deal?.id ?? ""),
        originalCurrency: origCurrency || null,
        originalTotal: Number.isFinite(origCurrencyTotal) ? origCurrencyTotal : null,
        expectedField: "ourTotalFare.origCurrency.priceWithCommissionAndCreditCardFee",
      });
    }
    const usdRate = Number(currencyRate?.USD) || null;
    const displayedIls =
      displayedUsd != null && usdRate && Number.isFinite(usdRate)
        ? displayedUsd * usdRate
        : null;
    const displayedPrice = {
      usdMain:
        displayedUsd != null && Number.isFinite(displayedUsd)
          ? `$${Math.round(displayedUsd)}`
          : "price unavailable",
      ilsSecondary:
        displayedIls != null && Number.isFinite(displayedIls)
          ? `(${convertPrice(displayedIls)})`
          : null,
      displayedUsdRaw: displayedUsd,
      displayedIlsRaw: displayedIls,
      sourceFieldUsedForDisplay,
      label: "per pax",
    };
    if (process.env.NODE_ENV === "development") {
      console.log("[FLIGHT_RESULT_PRICE_DEBUG]", {
        rawPrice: safeRaw,
        passengersCount,
        calculatedPerPersonPrice,
        displayedPrice,
      });
      console.log("[FLIGHT_CURRENCY_MODE_DEBUG]", {
        baseUsdPerPerson: displayedUsd,
        displayedUsd:
          displayedUsd != null && Number.isFinite(displayedUsd)
            ? Math.round(displayedUsd)
            : null,
        displayedIls,
        usdRate,
      });
    }
    return displayedPrice;
  };

  const emitFlightPriceBreakdownDebug = ({ deal, price, rawTotalIls }) => {
    if (process.env.NODE_ENV !== "development") return;
    const safeRawTotalIls = Number(rawTotalIls) || 0;
    const passengersCount = passengersCountForPricing || 1;
    const baseUsdTotal = Number(deal?.dealFare?.totalFare) || 0;
    const baseUsdPerPerson = baseUsdTotal / passengersCount;
    const usdRate = Number(currencyRate?.USD) || null;
    const baseIlsPerPerson =
      usdRate && Number.isFinite(usdRate) ? baseUsdPerPerson * usdRate : null;
    const creditCardFee = (Number(price?.ourTotalFare?.creditCardFee) || 0) / passengersCount;
    const systemFee = (Number(price?.ourTotalFare?.handlingFee) || 0) / passengersCount;
    const agentMarkup = (Number(price?.ourTotalFare?.agentCommission) || 0) / passengersCount;
    const hrMarkup = (Number(price?.ourTotalFare?.hrCommission) || 0) / passengersCount;
    const finalIlsPerPerson = safeRawTotalIls / passengersCount;
    console.log("[FLIGHT_PRICE_BREAKDOWN_DEBUG]", {
      baseUsdPerPerson,
      usdRate,
      baseIlsPerPerson,
      creditCardFee,
      systemFee,
      agentMarkup,
      hrMarkup,
      finalIlsPerPerson,
      passengersCount,
      rawTotalIls: safeRawTotalIls,
    });
  };

  const emitFlightPriceExtraSourceDebug = ({ deal, price, rawTotalIls, displayedIlsPerPerson }) => {
    if (process.env.NODE_ENV !== "development") return;
    const passengersCount = passengersCountForPricing || 1;
    const dealId = String(deal?.dealId ?? deal?.ID ?? deal?.id ?? "");
    const flightKey = String(
      deal?.linkedFlights?.[0]?.flight?.[0]?.leg?.flightNumber ??
        deal?.linkedFlights?.flight?.[0]?.leg?.flightNumber ??
        ""
    );
    const supplierUsdTotal = Number(deal?.dealFare?.totalFare) || 0;
    const supplierUsdPerPerson = supplierUsdTotal / passengersCount;
    const usdRate = Number(currencyRate?.USD) || null;
    const expectedIlsPerPersonFromUsd =
      usdRate && Number.isFinite(usdRate) ? supplierUsdPerPerson * usdRate : null;
    const rawIlsTotalNum = Number(rawTotalIls) || 0;
    const rawIlsPerPerson = rawIlsTotalNum / passengersCount;
    const priceWithCommission = Number(price?.ourTotalFare?.priceWithCommission) || 0;
    const priceWithCommissionAndCreditCardFee =
      Number(price?.ourTotalFare?.priceWithCommissionAndCreditCardFee) || 0;
    const creditCardFeeAmount = Number(price?.ourTotalFare?.creditCardFee) || 0;
    const hrCommissionAmount = Number(price?.ourTotalFare?.hrCommission) || 0;
    const agentCommissionAmount = Number(price?.ourTotalFare?.agentCommission) || 0;
    const commissionAmount = hrCommissionAmount + agentCommissionAmount;
    const systemFee = Number(price?.ourTotalFare?.handlingFee) || 0;
    const roundingDelta =
      rawIlsPerPerson - (priceWithCommission + creditCardFeeAmount) / passengersCount;
    const finalDisplayedIlsPerPerson = Number(displayedIlsPerPerson) || rawIlsPerPerson;
    const deltaFromExpectedIls =
      expectedIlsPerPersonFromUsd != null
        ? finalDisplayedIlsPerPerson - expectedIlsPerPersonFromUsd
        : null;

    console.log("[FLIGHT_PRICE_EXTRA_SOURCE_DEBUG]", {
      dealId,
      flightKey,
      passengersCount,
      supplierUsdTotal,
      supplierUsdPerPerson,
      usdRate,
      expectedIlsPerPersonFromUsd,
      rawIlsTotal: rawIlsTotalNum,
      rawIlsPerPerson,
      priceWithCommission,
      priceWithCommissionAndCreditCardFee,
      creditCardFeeAmount,
      commissionAmount,
      hrCommissionAmount,
      agentCommissionAmount,
      roundingDelta,
      finalDisplayedIlsPerPerson,
      deltaFromExpectedIls,
      systemFee,
    });
  };

  const emitFlightSsotPricingAudit = ({ deal, price, priceDisplay }) => {
    if (process.env.NODE_ENV !== "development") return;
    const pax = passengersCountForPricing || 1;
    const user = getUserDetails() || {};
    const fareCurrency = String(deal?.fareDetails?.currency || "").trim().toUpperCase();
    const apiGrossRaw = Number(deal?.dealFare?.totalFare) || 0;
    const apiGrossUsdTotal = fareCurrency === "USD" ? apiGrossRaw : null;
    const apiGrossUsdPerPerson =
      apiGrossUsdTotal != null ? apiGrossUsdTotal / pax : null;

    const supplierRule = price?.ldsCommissionCondition || null;
    const fareCommission = deal?.fareDetails?.commission || {};
    const supplierCommissionType = String(
      supplierRule?.commission_type || fareCommission?.commissionType || ""
    )
      .trim()
      .toUpperCase() || null;
    const supplierCommissionValue =
      supplierCommissionType === "PERCENT"
        ? Number(
            (supplierRule?.commission_percentage || 0) +
              (supplierRule?.discount_percentage || 0) ||
              fareCommission?.commissionValue ||
              0
          )
        : supplierCommissionType === "FIXED_PER_PERSON"
          ? Number(supplierRule?.fixed_commission_per_person || 0)
          : 0;
    let supplierCommissionAmountUsd = 0;
    if (supplierCommissionType === "PERCENT" && apiGrossUsdTotal != null) {
      supplierCommissionAmountUsd = (apiGrossUsdTotal * supplierCommissionValue) / 100;
    }
    const netUsdAfterSupplierCommission =
      apiGrossUsdTotal != null
        ? apiGrossUsdTotal - supplierCommissionAmountUsd
        : null;

    const origCurrencyVm = price?.ourTotalFare?.origCurrency || null;
    const systemMarginType = "PERCENT";
    const systemMarginValue =
      Number(origCurrencyVm?.hrCommissionPercent) ||
      Number(price?.ourTotalFare?.hrCommissionPercent) ||
      0;
    const systemMarginAmountUsd =
      Number(origCurrencyVm?.hrCommission) ||
      (Number(price?.ourTotalFare?.hrCommission) || 0) /
        (Number(price?.ourTotalFare?.fx_rate) || Number(currencyRate?.USD) || 1);
    const agentMarkupType = "PERCENT";
    const agentMarkupValue =
      Number(origCurrencyVm?.agentCommissionPercent) ||
      Number(price?.ourTotalFare?.agentCommissionPercent) ||
      0;
    const isPublicSiteContext =
      price?.ourTotalFare?.isPublicSite === true ||
      price?.ourTotalFare?.pricingContext === "public_site";
    const agentMarkupRawUsd =
      Number(origCurrencyVm?.agentCommission) ||
      (Number(price?.ourTotalFare?.agentCommission) || 0) /
        (Number(price?.ourTotalFare?.fx_rate) || Number(currencyRate?.USD) || 1);
    const agentMarkupAmountUsd = isPublicSiteContext ? agentMarkupRawUsd : 0;
    const creditCardFeeUsd =
      Number(origCurrencyVm?.creditCardFee) ||
      (Number(price?.ourTotalFare?.creditCardFee) || 0) /
        (Number(price?.ourTotalFare?.fx_rate) || Number(currencyRate?.USD) || 1);
    const finalClientUsdTotal =
      Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) ||
      ((Number(priceDisplay?.displayedUsdRaw) || 0) * pax);
    const finalClientUsdPerPerson = finalClientUsdTotal / pax;
    const displayedUsdPerPerson = Number(priceDisplay?.displayedUsdRaw) || null;
    const displayedIlsPerPerson = Number(priceDisplay?.displayedIlsRaw) || null;
    const supplierCommissionPerPersonUsd =
      supplierCommissionAmountUsd && Number.isFinite(supplierCommissionAmountUsd)
        ? supplierCommissionAmountUsd / pax
        : 0;
    const netUsdAfterSupplierCommissionPerPerson =
      netUsdAfterSupplierCommission != null
        ? Number(netUsdAfterSupplierCommission) / pax
        : null;
    const systemMarginPerPersonUsd =
      systemMarginAmountUsd && Number.isFinite(systemMarginAmountUsd)
        ? Number(systemMarginAmountUsd) / pax
        : 0;
    const agentMarkupPerPersonUsd =
      agentMarkupAmountUsd && Number.isFinite(agentMarkupAmountUsd)
        ? Number(agentMarkupAmountUsd) / pax
        : 0;
    const creditCardFeePerPersonUsd =
      creditCardFeeUsd && Number.isFinite(creditCardFeeUsd)
        ? Number(creditCardFeeUsd) / pax
        : 0;
    const baseWithoutRoundingPerPerson =
      (netUsdAfterSupplierCommissionPerPerson || 0) +
      systemMarginPerPersonUsd +
      agentMarkupPerPersonUsd +
      creditCardFeePerPersonUsd;
    const roundingDeltaUsd =
      displayedUsdPerPerson != null
        ? displayedUsdPerPerson - baseWithoutRoundingPerPerson
        : finalClientUsdPerPerson - baseWithoutRoundingPerPerson;
    const deltaFromApiGrossUsd =
      displayedUsdPerPerson != null
        ? displayedUsdPerPerson - apiGrossUsdPerPerson
        : finalClientUsdPerPerson - apiGrossUsdPerPerson;
    const deltaComponents = [
      {
        key: "supplierCommission",
        amountUsd: supplierCommissionPerPersonUsd ? -supplierCommissionPerPersonUsd : 0,
      },
      { key: "systemMargin", amountUsd: systemMarginPerPersonUsd },
      { key: "agentMarkup", amountUsd: agentMarkupPerPersonUsd },
      { key: "creditCardFee", amountUsd: creditCardFeePerPersonUsd },
      { key: "rounding", amountUsd: roundingDeltaUsd || 0 },
    ];
    const primaryDeltaDriver = deltaComponents
      .slice()
      .sort((a, b) => Math.abs(b.amountUsd) - Math.abs(a.amountUsd))[0];
    const deltaDriverLabelByKey = {
      supplierCommission: "supplier_commission",
      systemMargin: "system_margin",
      agentMarkup: "agent_markup",
      creditCardFee: "credit_card_fee",
      rounding: "rounding",
    };
    const deltaSource = deltaDriverLabelByKey[primaryDeltaDriver?.key] || "unknown";
    const traceSummary = [
      `apiGrossUsdPerPerson=${Number(apiGrossUsdPerPerson || 0).toFixed(2)}`,
      `supplierCommissionAmountUsd=${Number(-supplierCommissionPerPersonUsd || 0).toFixed(2)}`,
      `netUsdAfterSupplierCommission=${Number(netUsdAfterSupplierCommissionPerPerson || 0).toFixed(2)}`,
      `systemMarginAmountUsd=${Number(systemMarginPerPersonUsd || 0).toFixed(2)}`,
      `agentMarkupAmountUsd=${Number(agentMarkupPerPersonUsd || 0).toFixed(2)}`,
      `creditCardFeeUsd=${Number(creditCardFeePerPersonUsd || 0).toFixed(2)}`,
      `roundingDeltaUsd=${Number(roundingDeltaUsd || 0).toFixed(2)}`,
      `finalClientUsdPerPerson=${Number(finalClientUsdPerPerson || 0).toFixed(2)}`,
      `displayedUsdPerPerson=${Number(displayedUsdPerPerson || finalClientUsdPerPerson || 0).toFixed(2)}`,
      `deltaFromApiGrossUsd=${Number(deltaFromApiGrossUsd || 0).toFixed(2)}`,
      `deltaSource=${deltaSource}`,
    ].join(" | ");

    console.log("[FLIGHT_SSOT_PRICING_AUDIT]", {
      dealId: String(deal?.dealId ?? deal?.id ?? ""),
      agentId: user?.id ?? null,
      agentPackage: user?.package ?? user?.agent_package ?? user?.agentPackage ?? null,
      apiGrossUsdTotal,
      apiGrossUsdPerPerson,
      supplierCommissionType,
      supplierCommissionValue,
      supplierCommissionAmountUsd,
      supplierCommissionAmountUsdPerPerson: supplierCommissionPerPersonUsd,
      netUsdAfterSupplierCommission,
      netUsdAfterSupplierCommissionPerPerson,
      systemMarginType,
      systemMarginValue,
      systemMarginAmountUsd,
      systemMarginAmountUsdPerPerson: systemMarginPerPersonUsd,
      hrCommissionType: systemMarginType,
      hrCommissionValue: systemMarginValue,
      hrCommissionAmountUsd: systemMarginAmountUsd,
      hrCommissionAmountUsdPerPerson: systemMarginPerPersonUsd,
      agentMarkupType,
      agentMarkupValue,
      agentMarkupAmountUsd,
      agentMarkupAmountUsdPerPerson: agentMarkupPerPersonUsd,
      creditCardFeeUsd,
      creditCardFeeUsdPerPerson: creditCardFeePerPersonUsd,
      roundingDeltaUsd,
      finalClientUsdTotal,
      finalClientUsdPerPerson,
      displayedUsdPerPerson,
      deltaFromApiGrossUsd,
      deltaSource,
      traceSummary,
      displayedIlsPerPerson,
      usdRate: Number(currencyRate?.USD) || null,
      sourceFieldUsedForDisplay:
        priceDisplay?.sourceFieldUsedForDisplay || "finalClientUsd",
      pricingContext: isPublicSiteContext ? "public_site" : "agent_system",
    });
  };

  /** Keep linked-flight option index aligned with context legs (e.g. after package profile modal picks a deal). */
  useEffect(() => {
    if (!Array.isArray(linkedFlights) || linkedFlights.length === 0 || !selectedDepart) return;
    const matchIdx = linkedFlights.findIndex((lf) => {
      const d0 = lf?.flight?.[0]?.leg;
      const d1 = lf?.flight?.[1]?.leg;
      if (!d0) return false;
      const depMatch =
        d0.depDate === selectedDepart.depDate &&
        String(d0.flightNumber ?? "") === String(selectedDepart.flightNumber ?? "");
      if (!depMatch) return false;
      if (flightTripType === "OneWay") return true;
      if (!selectedReturn) return !d1;
      if (!d1) return false;
      return (
        d1.depDate === selectedReturn.depDate &&
        String(d1.flightNumber ?? "") === String(selectedReturn.flightNumber ?? "")
      );
    });
    if (matchIdx >= 0 && matchIdx !== selectedLinkedFlightIndex) {
      setSelectedLinkedFlightIndex(matchIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when context legs change; avoid loop with selectedLinkedFlightIndex
  }, [linkedFlights, selectedDepart, selectedReturn, flightTripType]);
  
  // Use STATE (not just ref) to track manually selected flights - persists across re-renders
  const [manuallySelectedFlights, setManuallySelectedFlights] = useState({ depart: null, return: null });
  const [manuallySelectedIndex, setManuallySelectedIndex] = useState(null);
  const [isManualSelection, setIsManualSelection] = useState(false);
  
  // Use ref to track if flights were manually selected (to prevent useEffect from overriding)
  const isManualSelectionRef = useRef(false);
  // Use ref to track previous linkedFlights to detect actual changes
  const previousLinkedFlightsRef = useRef(linkedFlights);
  // Use ref to track the manually selected flights to prevent reverting (backup)
  const manuallySelectedFlightsRef = useRef({ depart: null, return: null });
  // Use ref to track the manually selected index to prevent reverting (backup)
  const manuallySelectedIndexRef = useRef(null);

  useEffect(() => {
    if (savedDeal) {
      initLdsSupplements(savedDeal, initSupplements ? undefined : supplements);
    }
  }, [initSupplements, supplements, savedDeal]);

  // Reset selected flight index when linkedFlights changes
  // BUT only if it's not a manual selection and linkedFlights actually changed
  useEffect(() => {
    // Check if linkedFlights actually changed (not just reference)
    const linkedFlightsChanged = previousLinkedFlightsRef.current !== linkedFlights;
    
    // If there's a manual selection, restore the index instead of resetting
    if (linkedFlightsChanged && isManualSelectionRef.current && manuallySelectedIndexRef.current !== null) {
      // Restore the manually selected index
      setSelectedLinkedFlightIndex(manuallySelectedIndexRef.current);
      previousLinkedFlightsRef.current = linkedFlights;
      return;
    }
    
    // Only reset if:
    // 1. linkedFlights actually changed
    // 2. It's not a manual selection (or the manual selection flag has been cleared)
    if (linkedFlightsChanged && !isManualSelectionRef.current) {
      setSelectedLinkedFlightIndex(0);
      previousLinkedFlightsRef.current = linkedFlights;
    } else if (linkedFlightsChanged) {
      // Update the ref even if we don't reset, to track the change
      previousLinkedFlightsRef.current = linkedFlights;
    }
  }, [linkedFlights]); // Compact render for dynamic packages

  // Ref to track if we're currently updating flights to prevent infinite loops
  const isUpdatingFlightsRef = useRef(false);
  const lastUpdatedFlightsRef = useRef({ depart: null, return: null });

  // CRITICAL: Watch for changes to selectedDepart/selectedReturn and restore manual selection if needed
  // This is especially important for mobile where drawer closing might trigger re-renders
  useEffect(() => {
    // Skip if we're currently updating (prevent infinite loop)
    if (isUpdatingFlightsRef.current) {
      return;
    }

    // Use both state and ref for maximum reliability
    const hasManualSelection = isManualSelection || isManualSelectionRef.current;
    const manualFlights = manuallySelectedFlights.depart && manuallySelectedFlights.return 
      ? manuallySelectedFlights 
      : manuallySelectedFlightsRef.current;
    
    // Only act if there's a manual selection
    if (!hasManualSelection || !manualFlights.depart || !manualFlights.return) {
      return;
    }
    
    // CRITICAL: If we have a manual selection, NEVER let this effect update flights
    // This prevents the effect from overwriting the user's choice
    if (hasManualSelection) {
      return;
    }

    // Check if current flights don't match manual selection
    const currentMatches = 
      selectedDepart &&
      selectedReturn &&
      flightsMatch(selectedDepart, manualFlights.depart) &&
      flightsMatch(selectedReturn, manualFlights.return);

    // Also check if we just updated to these flights (prevent loop)
    const justUpdated = 
      lastUpdatedFlightsRef.current.depart &&
      lastUpdatedFlightsRef.current.return &&
      flightsMatch(lastUpdatedFlightsRef.current.depart, selectedDepart) &&
      flightsMatch(lastUpdatedFlightsRef.current.return, selectedReturn);

    // If they don't match and we didn't just update, restore the manual selection
    if (!currentMatches && !justUpdated) {
      console.log('[FlightSelection] Detected flight change - restoring manual selection', {
        current: { depart: selectedDepart?.depDate, return: selectedReturn?.depDate },
        manual: { depart: manualFlights.depart?.depDate, return: manualFlights.return?.depDate }
      });
      
      // Mark that we're updating
      isUpdatingFlightsRef.current = true;
      lastUpdatedFlightsRef.current = { depart: manualFlights.depart, return: manualFlights.return };
      
      // Restore immediately
      setSelectedDepart(manualFlights.depart);
      setSelectedReturn(manualFlights.return);
      
      const manualIndex = manuallySelectedIndex !== null 
        ? manuallySelectedIndex 
        : manuallySelectedIndexRef.current;
      if (manualIndex !== null) {
        setSelectedLinkedFlightIndex(manualIndex);
      }

      // Reset the flag after a short delay
      setTimeout(() => {
        isUpdatingFlightsRef.current = false;
      }, 100);
    }
  }, [selectedDepart, selectedReturn, isManualSelection, manuallySelectedFlights, manuallySelectedIndex]); // Watch for changes to flights

  // const renderCompactFlights = () => {
  //   // Handle different data structures
  //   let flightsToRender = null;

  //   if (linkedFlights) {
  //     if (Array.isArray(linkedFlights)) {
  //       flightsToRender = linkedFlights;
  //     } else if (linkedFlights.flight) {
  //       // Single flight object with flight property
  //       flightsToRender = linkedFlights.flight;
  //     } else {
  //       // Single flight object
  //       flightsToRender = [linkedFlights];
  //     }
  //   }

  //   if (
  //     !flightsToRender ||
  //     (Array.isArray(flightsToRender) && flightsToRender.length === 0)
  //   ) {
  //     return (
  //       <div className="compact-flights-empty">
  //         <Text type="secondary">{t("flights.noFlightsAvailable")}</Text>
  //       </div>
  //     );
  //   }

  //   return (
  //     <motion.div
  //       initial={{ opacity: 0 }}
  //       animate={{ opacity: 1 }}
  //       className="compact-flights-container"
  //     >
  //       {includeTitle ? (
  //         <div className="compact-flights-header">
  //           <Text strong className="compact-title">
  //             {t("flights.includedFlights")}
  //           </Text>
  //         </div>
  //       ) : null}
  //       <div className="compact-flights-list">
  //         {Array.isArray(flightsToRender) ? (
  //           flightsToRender.slice(0, 2).map((flight, index) => {
  //             // Handle different flight data structures
  //             let legs = [];
  //             if (flight && flight.leg) {
  //               legs = Array.isArray(flight.leg) ? flight.leg : [flight.leg];
  //             }

  //             return (
  //               <div key={index} className="compact-flight-item">
  //                 {legs.map((leg, legIndex) => {
  //                   if (!leg) return null;

  //                   const isOutbound = index === 0;
  //                   const depDate = leg.depDate
  //                     ? leg.depDate.split(" ")[1]
  //                     : "";
  //                   const arrDate = leg.arrDate
  //                     ? leg.arrDate.split(" ")[1]
  //                     : "";
  //                   const depDateString = leg.depDate
  //                     ? leg.depDate.split(" ")[0]
  //                     : "";
  //                   const arrDateString = leg.arrDate
  //                     ? leg.arrDate.split(" ")[0]
  //                     : "";

  //                   // Calculate duration display
  //                   const duration = leg.duration || 0;
  //                   const durationMin = duration % 60;
  //                   const durationHours = Math.floor(duration / 60);
  //                   const durationDisplay = `${durationHours}:${durationMin < 10 ? `0${durationMin}` : durationMin}`;

  //                   return (
  //                     <div key={legIndex} className="compact-flight-info">
  //                       <div className="flight-header">
  //                         <div className="flight-direction">
  //                           {isOutbound ? (
  //                             <>
  //                               <ArrowLeftOutlined /> טיסה הלוך
  //                             </>
  //                           ) : (
  //                             <>
  //                               <ArrowRightOutlined /> טיסה חזור
  //                             </>
  //                           )}
  //                         </div>
  //                         {/* Airline Details */}
  //                         <div className="flight-number">
  //                           {leg.flightNumber || ""}
  //                         </div>
  //                       </div>

  //                       <AirlineDisplay
  //                         airlineId={leg.airlineId}
  //                         flightNumber={leg.flightNumber}
  //                         isCompact={true}
  //                       />

  //                       <div
  //                         className={
  //                           "flight-route" + (!isOutbound ? " inbound" : "")
  //                         }
  //                       >
  //                         <div className="departure-info">
  //                           <div className="time">{depDate}</div>
  //                           <div className="date">{depDateString}</div>
  //                           <div className="airport">
  //                             {leg.depAirport?.code || leg.depCity || ""}
  //                           </div>
  //                         </div>

  //                         <div className="route-line">
  //                           <div className="duration-badge">
  //                             <ClockCircleOutlined />
  //                             {durationDisplay}
  //                           </div>
  //                           <div className="flight-path">
  //                             <div className="path-line"></div>
  //                             {isOutbound ? (
  //                               <ArrowLeftOutlined className="plane-icon" />
  //                             ) : (
  //                               <ArrowRightOutlined className="plane-icon" />
  //                             )}
  //                           </div>
  //                         </div>

  //                         <div className="arrival-info">
  //                           <div className="time">{arrDate}</div>
  //                           <div className="date">{arrDateString}</div>
  //                           <div className="airport">
  //                             {leg.arrAirport?.code || leg.arrCity || ""}
  //                           </div>
  //                         </div>
  //                       </div>

  //                       {leg.seats && (
  //                         <div className="flight-extras">
  //                           <span className="seats-info">
  //                             {leg.seats} מקומות זמינים
  //                           </span>
  //                         </div>
  //                       )}
  //                     </div>
  //                   );
  //                 })}
  //               </div>
  //             );
  //           })
  //         ) : (
  //           <div className="compact-flight-item">
  //             {(() => {
  //               // Handle single flight data structure
  //               let legs = [];
  //               if (flightsToRender && flightsToRender.leg) {
  //                 legs = Array.isArray(flightsToRender.leg)
  //                   ? flightsToRender.leg
  //                   : [flightsToRender.leg];
  //               }

  //               return legs.map((leg, legIndex) => {
  //                 if (!leg) return null;

  //                 const isOutbound = legIndex === 0;
  //                 const depDate = leg.depDate ? leg.depDate.split(" ")[1] : "";
  //                 const arrDate = leg.arrDate ? leg.arrDate.split(" ")[1] : "";
  //                 const depDateString = leg.depDate
  //                   ? leg.depDate.split(" ")[0]
  //                   : "";
  //                 const arrDateString = leg.arrDate
  //                   ? leg.arrDate.split(" ")[0]
  //                   : "";

  //                 // Calculate duration display
  //                 const duration = leg.duration || 0;
  //                 const durationMin = duration % 60;
  //                 const durationHours = Math.floor(duration / 60);
  //                 const durationDisplay = `${durationHours}:${durationMin < 10 ? `0${durationMin}` : durationMin}`;

  //                 return (
  //                   <div key={legIndex} className="compact-flight-info">
  //                     <div className="flight-header">
  //                       <div className="flight-direction">
  //                         {isOutbound ? (
  //                           <>
  //                             <ArrowLeftOutlined /> טיסה הלוך
  //                           </>
  //                         ) : (
  //                           <>
  //                             <ArrowRightOutlined /> טיסה חזור
  //                           </>
  //                         )}
  //                       </div>
  //                       <div className="flight-number">
  //                         {leg.flightNumber || ""}
  //                       </div>

  //                       {/* Seats and class info moved to header */}
  //                       <div className="flight-header-extras">
  //                         {leg.seats && (
  //                           <span className="seats-info">
  //                             {leg.seats} מקומות זמינים
  //                           </span>
  //                         )}
  //                         {leg.flightClass && (
  //                           <span className="flight-class-info">
  //                             {leg.flightClass}
  //                           </span>
  //                         )}
  //                       </div>
  //                     </div>

  //                     <div className="flight-route">
  //                       <div className="departure-info">
  //                         <div className="time">{depDate}</div>
  //                         <div className="date">{depDateString}</div>
  //                         <div className="airport">
  //                           {leg.depAirport?.code || leg.depCity || ""}
  //                         </div>
  //                       </div>

  //                       <div className="route-line">
  //                         <div className="duration-badge">
  //                           <ClockCircleOutlined />
  //                           {durationDisplay}
  //                         </div>
  //                         <div className="flight-path">
  //                           <div className="path-line"></div>
  //                           {isOutbound ? (
  //                             <ArrowLeftOutlined className="plane-icon" />
  //                           ) : (
  //                             <ArrowRightOutlined className="plane-icon" />
  //                           )}
  //                         </div>
  //                       </div>

  //                       <div className="arrival-info">
  //                         <div className="time">{arrDate}</div>
  //                         <div className="date">{arrDateString}</div>
  //                         <div className="airport">
  //                           {leg.arrAirport?.code || leg.arrCity || ""}
  //                         </div>
  //                       </div>
  //                     </div>

  //                     {/* Status remarks */}
  //                     {(leg.statusRemark || leg.flightCategory) && (
  //                       <div className="flight-status-compact">
  //                         <IoInformationCircleOutline size={12} />
  //                         <span className="status-text">
  //                           {leg.flightCategory || leg.statusRemark}
  //                         </span>
  //                       </div>
  //                     )}
  //                   </div>
  //                 );
  //               });
  //             })()}
  //           </div>
  //         )}
  //       </div>
  //     </motion.div>
  //   );
  // };

  const renderFlights = () => {
    // If showOnlyButton is true, only show the button without the flight display
    if (showOnlyButton) {
      return (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`flight-selection-container ${inTourObject ? "in-tour-object" : ""} ${isCompact ? "compact-mode" : ""}`}
          >
            {/* Only show button if there are multiple flights for this specific package */}
            {!hideSelectionTrigger &&
            (forceShowButton || (isSelectable &&
              !linkedFlightsData &&
              Array.isArray(availableFlights) &&
              availableFlights.length - 1 > 0) ||
            (Array.isArray(linkedFlights) && linkedFlights.length > 1)) ? (
              <Button
                type="default"
                size="large"
                onClick={() => setIsSelectorOpen(true)}
                className="btn btn-secondary btn-icon flight-selection-trigger-btn"
              >
                <FaPlane className="tw-text-base" />
                {customButtonText || (
                  isSelectable &&
                  !linkedFlightsData &&
                  Array.isArray(availableFlights) &&
                  availableFlights.length - 1 > 0
                    ? t("flights.viewMoreFlights", {
                        count: availableFlights.length - 1,
                      })
                    : forceShowButton
                      ? t("flights.changeFlights")
                      : t("flights.viewMoreFlights", {
                        count: Math.max(0, linkedFlights.length - 1),
                      })
                )}
              </Button>
            ) : null}
          </motion.div>
          {/* Drawer for flight selection - must be included even when showOnlyButton is true */}
          <Drawer
            title={
              <div className="flight-change-drawer-header">
                <div className="drawer-header-icon">
                  <SwitcherOutlined />
                </div>
                <div className="drawer-header-content">
                  <span className="drawer-title">{t("flights.changeFlights")}</span>
                  <span className="drawer-subtitle">בחר את הטיסה המתאימה לך</span>
                </div>
              </div>
            }
            placement={isMobile ? "bottom" : "left"}
            onClose={() => setIsSelectorOpen(false)}
            afterClose={() => {
              // After drawer closes on mobile, restore manual selection if it exists
              if (isMobile) {
                // Use both state and ref for maximum reliability
                const hasManualSelection = isManualSelection || isManualSelectionRef.current;
                const manualFlights = manuallySelectedFlights.depart && manuallySelectedFlights.return
                  ? manuallySelectedFlights
                  : manuallySelectedFlightsRef.current;
                
                if (hasManualSelection && manualFlights.depart && manualFlights.return) {
                  // Restore the manually selected flights after drawer animation completes
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      if (manualFlights.depart && manualFlights.return) {
                        setSelectedDepart(manualFlights.depart);
                        setSelectedReturn(manualFlights.return);
                        const manualIndex = manuallySelectedIndex !== null
                          ? manuallySelectedIndex
                          : manuallySelectedIndexRef.current;
                        if (manualIndex !== null) {
                          setSelectedLinkedFlightIndex(manualIndex);
                        }
                      }
                    });
                  });
                }
              }
            }}
            open={isSelectorOpen}
            width={isMobile ? "100%" : 600}
            height={isMobile ? "90vh" : "100vh"}
            className="flight-change-drawer"
            getContainer={() => document.body}
            zIndex={isMobile ? 10000001 : 1000}
            styles={{
              body: {
                padding: "0",
                overflow: "auto",
              },
              header: {
                padding: isMobile ? "16px 20px" : "20px 24px",
                borderBottom: "none",
                background: "linear-gradient(135deg, #0456A0 0%, #064e8e 100%)",
                color: "white",
                zIndex: isMobile ? 999999 : "auto",
                boxShadow: "0 2px 8px rgba(4, 86, 160, 0.2)",
              },
              mask: {
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                backdropFilter: "blur(2px)",
                zIndex: isMobile ? 999998 : "auto",
              },
              content: {
                boxShadow: isMobile 
                  ? "0 -4px 24px rgba(4, 86, 160, 0.2)" 
                  : "-4px 0 24px rgba(4, 86, 160, 0.2)",
                zIndex: isMobile ? 999999 : "auto",
              },
              wrapper: {
                zIndex: isMobile ? 10000001 : "auto",
              },
            }}
            closeIcon={
              <div className="drawer-close-icon">
                <span>✕</span>
              </div>
            }
          >
            <div className="flight-options-container">
              {/* Flight Sort/Filter Options - Show if there are multiple flights to choose from */}
              {isSelectable && (
                (Array.isArray(availableFlights) && availableFlights.length > 1 && !linkedFlightsData) ||
                (Array.isArray(linkedFlights) && linkedFlights.length > 1 && (!availableFlights || availableFlights.length <= 1))
              ) && (
                <div className="flight-sort-container" style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  direction: 'rtl'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>מיין לפי:</span>
                  <Select
                    value={sortBy}
                    onChange={setSortBy}
                    style={{ width: 150 }}
                    options={[
                      { value: 'price', label: 'מחיר (נמוך לגבוה)' },
                      { value: 'time', label: 'שעת יציאה' },
                      { value: 'duration', label: 'משך טיסה' },
                    ]}
                  />
                </div>
              )}
              {/* Show availableFlights if it's a selectable context with multiple deals */}
              {/* Only use availableFlights if linkedFlightsData is not provided (shared context) */}
              {isSelectable &&
              !linkedFlightsData &&
              Array.isArray(availableFlights) &&
              availableFlights.length > 1
                ? availableFlights
                    .sort((a, b) => {
                      if (sortBy === "price") {
                        const aPrice = calculatFlightLDSPrice(a);
                        const bPrice = calculatFlightLDSPrice(b);
                        return aPrice - bPrice;
                      } else if (sortBy === "time") {
                        // Sort by departure time
                        const aTime = a.linkedFlights?.[0]?.flight?.[0]?.leg?.depDate || "";
                        const bTime = b.linkedFlights?.[0]?.flight?.[0]?.leg?.depDate || "";
                        return aTime.localeCompare(bTime);
                      } else if (sortBy === "duration") {
                        // Sort by total flight duration
                        const aDuration = a.linkedFlights?.[0]?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                        const bDuration = b.linkedFlights?.[0]?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                        return aDuration - bDuration;
                      }
                      return 0;
                    })
                    .map((deal, index) => {
                      // Ensure category is passed to calculateLDSDealFare
                      const dealWithCategory = {
                        ...deal,
                        dealCategory: deal?.category || (categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null),
                      };
                      const price = calculateLDSDealFare(dealWithCategory);
                      const total_fare =
                        price.ourTotalFare
                          ?.priceWithCommissionAndCreditCardFee || 0;
                      emitFlightPriceBreakdownDebug({
                        deal,
                        price,
                        rawTotalIls: total_fare,
                      });
                      const priceDisplay = buildFlightResultPriceDisplay({
                        rawPrice: total_fare,
                        price,
                        deal,
                      });
                      emitFlightPriceExtraSourceDebug({
                        deal,
                        price,
                        rawTotalIls: total_fare,
                        displayedIlsPerPerson:
                          Number(priceDisplay?.displayedIlsRaw) ||
                          Number(total_fare) / (passengersCountForPricing || 1),
                      });
                      emitFlightSsotPricingAudit({
                        deal,
                        price,
                        priceDisplay,
                      });
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.3,
                            delay: index * 0.1,
                          }}
                        >
                          <Card
                            hoverable
                            className={`flight-option-card compact-flight-card ${
                              index === 0 ? "best-price-card" : ""
                            }`}
                            styles={{
                              body: { padding: isMobile ? "8px" : "12px" },
                            }}
                          >
                            <div className="flight-option-content">
                              {isCompact
                                ? renderFlightsOnlyCompact(
                                    deal.linkedFlights
                                  )
                                : renderFlightsOnly(deal.linkedFlights)}
                            </div>

                            <Divider className="tw-my-4" />

                            <div className="flight-option-footer compact-footer">
                              <div className="price-section">
                                <motion.div
                                  className="price-display price-highlighted-box"
                                  whileHover={{ scale: 1.01 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <div className="price-single-row">
                                    <div className="price-label-wrapper">
                                      <span className="price-label">{priceDisplay.label}</span>
                                    </div>
                                    {index === 0 ? (
                                      <Badge
                                        count={t("flights.bestPrice")}
                                        className="price-badge compact-badge hide-on-mobile"
                                      />
                                    ) : null}
                                    <Text className="price-amount compact-price" style={{ direction: "ltr", unicodeBidi: "plaintext" }}>
                                      {priceDisplay.usdMain}
                                    </Text>
                                    <div className="price-usd-inline">
                                      <Text className="price-usd compact-price-usd">
                                        {priceDisplay.ilsSecondary}
                                      </Text>
                                    </div>
                                  </div>
                                </motion.div>
                              </div>
                              <motion.div
                                className="motion-wrapper"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Button
                                  type="primary"
                                  icon={<CheckCircleOutlined />}
                                  style={{
                                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                    border: 'none',
                                    margin: '24px auto 0 auto',
                                    minWidth: '200px',
                                    display: 'flex',
                                    justifyContent: 'center'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    // Find the index of this deal in availableFlights
                                    const dealIndex = availableFlights?.findIndex(d => d === deal) ?? index;
                                    
                                    // Check if deal has linkedFlights as array
                                    let departLeg = null;
                                    let returnLeg = null;
                                    
                                    if (Array.isArray(deal.linkedFlights) && deal.linkedFlights.length > 0) {
                                      const firstLinkedFlight = deal.linkedFlights[0];
                                      if (firstLinkedFlight?.flight?.[0]?.leg && firstLinkedFlight?.flight?.[1]?.leg) {
                                        departLeg = firstLinkedFlight.flight[0].leg;
                                        returnLeg = firstLinkedFlight.flight[1].leg;
                                      }
                                    } else if (deal.linkedFlights?.flight) {
                                      // Check if deal has linkedFlights.flight directly
                                      const flightsArray = deal.linkedFlights.flight;
                                      if (Array.isArray(flightsArray) && flightsArray.length >= 2) {
                                        if (flightsArray[0]?.leg && flightsArray[1]?.leg) {
                                          departLeg = flightsArray[0].leg;
                                          returnLeg = flightsArray[1].leg;
                                        }
                                      }
                                    }
                                    
                                    if (departLeg && returnLeg) {
                                      // CRITICAL: Set manual selection flags FIRST before any updates
                                      // This prevents useEffect from overwriting the selection
                                      // Use ref FIRST to ensure it's set before any effects run
                                      isManualSelectionRef.current = true;
                                      setIsManualSelection(true);
                                      
                                      // CRITICAL: Save manual selection in BOTH state and ref
                                      const manualFlights = { depart: departLeg, return: returnLeg };
                                      manuallySelectedFlightsRef.current = manualFlights;
                                      manuallySelectedIndexRef.current = dealIndex;
                                      setManuallySelectedFlights(manualFlights);
                                      setManuallySelectedIndex(dealIndex);
                                      previousLinkedFlightsRef.current = linkedFlights;
                                      
                                      // Update flights IMMEDIATELY - do this BEFORE setSavedDeal
                                      // to ensure the UI updates immediately
                                      setSelectedDepart(departLeg);
                                      setSelectedReturn(returnLeg);
                                      setSelectedLinkedFlightIndex(dealIndex);
                                      
                                      // Update linkedFlights in context
                                      setSavedDeal(deal);
                                      
                                      // Close drawer - use requestAnimationFrame for mobile
                                      if (isMobile) {
                                        requestAnimationFrame(() => {
                                          requestAnimationFrame(() => {
                                            setIsSelectorOpen(false);
                                          });
                                        });
                                      } else {
                                        setIsSelectorOpen(false);
                                      }
                                    }
                                  }}
                                  className="choose-flight-btn compact-btn"
                                  size={isMobile ? "small" : "default"}
                                  block={isMobile}
                                >
                                  {t("flights.chooseFlight")}
                                </Button>
                              </motion.div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    })
                : /* Show linkedFlights when there are multiple linked flights but no availableFlights */
                  Array.isArray(linkedFlights) && linkedFlights.length > 1 ? (
                    <>
                      {/* Flight Sort/Filter Options for linkedFlights */}
                      <div className="flight-sort-container" style={{ 
                        padding: '16px 20px', 
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        justifyContent: 'flex-end',
                        direction: 'rtl'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>מיין לפי:</span>
                        <Select
                          value={sortBy}
                          onChange={setSortBy}
                          style={{ width: 150 }}
                          options={[
                            { value: 'price', label: 'מחיר (נמוך לגבוה)' },
                            { value: 'time', label: 'שעת יציאה' },
                            { value: 'duration', label: 'משך טיסה' },
                          ]}
                        />
                      </div>
                      {linkedFlights
                        .sort((a, b) => {
                          if (sortBy === "price") {
                            const aPrice = calculatFlightLDSPrice(a);
                            const bPrice = calculatFlightLDSPrice(b);
                            return aPrice - bPrice;
                          } else if (sortBy === "time") {
                            // Sort by departure time
                            const aTime = a?.flight?.[0]?.leg?.depDate || "";
                            const bTime = b?.flight?.[0]?.leg?.depDate || "";
                            return aTime.localeCompare(bTime);
                          } else if (sortBy === "duration") {
                            // Sort by total flight duration
                            const aDuration = a?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                            const bDuration = b?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                            return aDuration - bDuration;
                          }
                          return 0;
                        })
                        .map((linkedFlight, index) => {
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.3,
                            delay: index * 0.1,
                          }}
                        >
                          <Card
                            hoverable
                            className={`flight-option-card compact-flight-card ${
                              index === selectedLinkedFlightIndex
                                ? "selected-flight-card"
                                : ""
                            }`}
                            styles={{
                              body: { padding: isMobile ? "8px" : "12px" },
                            }}
                          >
                            <div className="flight-option-content">
                              {isCompact
                                ? renderFlightsOnlyCompact(linkedFlight)
                                : renderFlightsOnly(linkedFlight)}
                            </div>

                            <Divider className="tw-my-4" />

                            <div className="flight-option-footer compact-footer">
                              <motion.div
                                className="motion-wrapper"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Button
                                  type={
                                    index === selectedLinkedFlightIndex
                                      ? "primary"
                                      : "default"
                                  }
                                  icon={
                                    index ===
                                    selectedLinkedFlightIndex ? (
                                      <CheckCircleOutlined />
                                    ) : null
                                  }
                                  style={index === selectedLinkedFlightIndex ? {
                                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                    border: 'none',
                                    margin: '24px auto 0 auto',
                                    minWidth: '200px',
                                    display: 'flex',
                                    justifyContent: 'center'
                                  } : {
                                    margin: '24px auto 0 auto',
                                    minWidth: '200px',
                                    display: 'flex',
                                    justifyContent: 'center'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    if (linkedFlight?.flight?.[0]?.leg && linkedFlight?.flight?.[1]?.leg) {
                                      const departLeg = linkedFlight.flight[0].leg;
                                      const returnLeg = linkedFlight.flight[1].leg;
                                      
                                      // CRITICAL: Save manual selection FIRST, before any state updates
                                      manuallySelectedFlightsRef.current = {
                                        depart: departLeg,
                                        return: returnLeg
                                      };
                                      manuallySelectedIndexRef.current = index;
                                      isManualSelectionRef.current = true;
                                      previousLinkedFlightsRef.current = linkedFlights;
                                      
                                      // Update flights IMMEDIATELY - use setTimeout to ensure refs are set first
                                      setTimeout(() => {
                                        setSelectedDepart(departLeg);
                                        setSelectedReturn(returnLeg);
                                        setSelectedLinkedFlightIndex(index);
                                      }, 0);
                                      
                                      // Get flight times for notification
                                      const departTime = departLeg?.depDate?.split(" ")[1] || "";
                                      const returnTime = returnLeg?.depDate?.split(" ")[1] || "";
                                      
                                      // Close drawer after a tiny delay to ensure state is updated
                                      setTimeout(() => {
                                        setIsSelectorOpen(false);
                                        
                                        // Show notification after drawer closes
                                        try {
                                          notification.success({
                                            message: "✓ הטיסה התחלפה בהצלחה",
                                            description: departTime && returnTime 
                                              ? `טיסה הלוך: ${departTime} | טיסה חזור: ${returnTime}`
                                              : "הטיסה נבחרה בהצלחה",
                                            duration: 4,
                                            placement: "topRight",
                                            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                            style: {
                                              zIndex: 10000002,
                                            },
                                          });
                                        } catch (error) {
                                          console.error("Error showing notification:", error);
                                        }
                                      }, 10);
                                    }
                                  }}
                                  className="choose-flight-btn compact-btn"
                                  size={isMobile ? "small" : "default"}
                                >
                                  {index === selectedLinkedFlightIndex
                                    ? t("flights.selected", "נבחר")
                                    : t("flights.chooseFlight")}
                                </Button>
                              </motion.div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    })}
                    </>
                  ) : null}
            </div>
          </Drawer>
        </>
      );
    }
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`flight-selection-container ${inTourObject ? "in-tour-object" : ""} ${isCompact ? "compact-mode" : ""}`}
      >
        {isCompact
          ? renderFlightsOnlyCompact(linkedFlights)
          : renderFlightsOnly(linkedFlights)}
        {/* Only show button if there are multiple flights for this specific package */}
        {!hideSelectionTrigger &&
        (forceShowButton || (isSelectable &&
          !linkedFlightsData &&
          Array.isArray(availableFlights) &&
          availableFlights.length - 1 > 0) ||
        (Array.isArray(linkedFlights) && linkedFlights.length > 1)) ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="tw-my-3"
          >
            <Button
              type="default"
              size="large"
              onClick={() => setIsSelectorOpen(true)}
              className="btn btn-secondary btn-icon flight-selection-trigger-btn"
            >
              <FaPlane className="tw-text-lg" />
              {isSelectable &&
              !linkedFlightsData &&
              Array.isArray(availableFlights) &&
              availableFlights.length - 1 > 0
                ? t("flights.viewMoreFlights", {
                    count: availableFlights.length - 1,
                  })
                : forceShowButton
                  ? t("flights.changeFlights")
                  : t("flights.viewMoreFlights", {
                    count: Math.max(0, linkedFlights.length - 1),
                  })}
            </Button>
          </motion.div>
        ) : null}
        <Drawer
          title={
            <div className="flight-change-drawer-header">
              <div className="drawer-header-icon">
                <SwitcherOutlined />
              </div>
              <div className="drawer-header-content">
                <span className="drawer-title">{t("flights.changeFlights")}</span>
                <span className="drawer-subtitle">בחר את הטיסה המתאימה לך</span>
              </div>
            </div>
          }
          placement={isMobile ? "bottom" : "left"}
          onClose={() => setIsSelectorOpen(false)}
          afterClose={() => {
            // After drawer closes on mobile, restore manual selection if it exists
            if (isMobile) {
              // Use both state and ref for maximum reliability
              const hasManualSelection = isManualSelection || isManualSelectionRef.current;
              const manualFlights = manuallySelectedFlights.depart && manuallySelectedFlights.return
                ? manuallySelectedFlights
                : manuallySelectedFlightsRef.current;
              
              if (hasManualSelection && manualFlights.depart && manualFlights.return) {
                // Restore the manually selected flights after drawer animation completes
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    if (manualFlights.depart && manualFlights.return) {
                      setSelectedDepart(manualFlights.depart);
                      setSelectedReturn(manualFlights.return);
                      const manualIndex = manuallySelectedIndex !== null
                        ? manuallySelectedIndex
                        : manuallySelectedIndexRef.current;
                      if (manualIndex !== null) {
                        setSelectedLinkedFlightIndex(manualIndex);
                      }
                    }
                  });
                });
              }
            }
          }}
          open={isSelectorOpen}
          width={isMobile ? "100%" : 600}
          height={isMobile ? "90vh" : "100vh"}
          className="flight-change-drawer"
          getContainer={() => document.body}
          zIndex={isMobile ? 10000001 : 1000}
          styles={{
            body: {
              padding: "0",
              overflow: "auto",
            },
            header: {
              padding: isMobile ? "16px 20px" : "20px 24px",
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              zIndex: isMobile ? 999999 : "auto",
            },
            mask: {
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              zIndex: isMobile ? 999998 : "auto",
            },
            content: {
              boxShadow: isMobile 
                ? "0 -4px 24px rgba(0, 0, 0, 0.15)" 
                : "-4px 0 24px rgba(0, 0, 0, 0.15)",
              zIndex: isMobile ? 999999 : "auto",
            },
            wrapper: {
              zIndex: isMobile ? 10000001 : "auto",
            },
          }}
          closeIcon={
            <div className="drawer-close-icon">
              <span>✕</span>
            </div>
          }
        >
            <div className="flight-options-container">
              {/* Flight Sort/Filter Options - Show if there are multiple flights to choose from */}
              {isSelectable && (
                (Array.isArray(availableFlights) && availableFlights.length > 1 && !linkedFlightsData) ||
                (Array.isArray(linkedFlights) && linkedFlights.length > 1 && (!availableFlights || availableFlights.length <= 1))
              ) && (
                <div className="flight-sort-container" style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  direction: 'rtl'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>מיין לפי:</span>
                  <Select
                    value={sortBy}
                    onChange={setSortBy}
                    style={{ width: 150 }}
                    options={[
                      { value: 'price', label: 'מחיר (נמוך לגבוה)' },
                      { value: 'time', label: 'שעת יציאה' },
                      { value: 'duration', label: 'משך טיסה' },
                    ]}
                  />
                </div>
              )}
              {/* Show availableFlights if it's a selectable context with multiple deals */}
              {/* Only use availableFlights if linkedFlightsData is not provided (shared context) */}
              {isSelectable &&
              !linkedFlightsData &&
              Array.isArray(availableFlights) &&
              availableFlights.length > 1
                ? availableFlights
                    .sort((a, b) => {
                      if (sortBy === "price") {
                        const aPrice = calculatFlightLDSPrice(a);
                        const bPrice = calculatFlightLDSPrice(b);
                        return aPrice - bPrice;
                      } else if (sortBy === "time") {
                        // Sort by departure time
                        const aTime = a.linkedFlights?.[0]?.flight?.[0]?.leg?.depDate || "";
                        const bTime = b.linkedFlights?.[0]?.flight?.[0]?.leg?.depDate || "";
                        return aTime.localeCompare(bTime);
                      } else if (sortBy === "duration") {
                        // Sort by total flight duration
                        const aDuration = a.linkedFlights?.[0]?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                        const bDuration = b.linkedFlights?.[0]?.flight?.reduce((sum, f) => sum + (f.leg?.duration || 0), 0) || 0;
                        return aDuration - bDuration;
                      }
                      return 0;
                    })
                          .map((deal, index) => {
                            // Ensure category is passed to calculateLDSDealFare
                            const dealWithCategory = {
                              ...deal,
                              dealCategory: deal?.category || (categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null),
                            };
                            const price = calculateLDSDealFare(dealWithCategory);
                            const total_fare =
                              price.ourTotalFare
                                ?.priceWithCommissionAndCreditCardFee || 0;
                            emitFlightPriceBreakdownDebug({
                              deal,
                              price,
                              rawTotalIls: total_fare,
                            });
                            const priceDisplay = buildFlightResultPriceDisplay({
                              rawPrice: total_fare,
                              price,
                              deal,
                            });
                            emitFlightPriceExtraSourceDebug({
                              deal,
                              price,
                              rawTotalIls: total_fare,
                              displayedIlsPerPerson:
                                Number(priceDisplay?.displayedIlsRaw) ||
                                Number(total_fare) / (passengersCountForPricing || 1),
                            });
                            emitFlightSsotPricingAudit({
                              deal,
                              price,
                              priceDisplay,
                            });
                            return (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  duration: 0.3,
                                  delay: index * 0.1,
                                }}
                              >
                                <Card
                                  hoverable
                                  className={`flight-option-card compact-flight-card ${
                                    index === 0 ? "best-price-card" : ""
                                  }`}
                                  styles={{
                                    body: { padding: isMobile ? "8px" : "12px" },
                                  }}
                                >
                                  <div className="flight-option-content">
                                    {isCompact
                                      ? renderFlightsOnlyCompact(
                                          deal.linkedFlights
                                        )
                                      : renderFlightsOnly(deal.linkedFlights)}
                                  </div>

                                  <Divider className="tw-my-4" />

                                  <div className="flight-option-footer compact-footer">
                                    <div className="price-section">
                                      <motion.div
                                        className="price-display price-highlighted-box"
                                        whileHover={{ scale: 1.01 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <div className="price-single-row">
                                          <div className="price-label-wrapper">
                                            <span className="price-label">{priceDisplay.label}</span>
                                          </div>
                                          {index === 0 ? (
                                            <Badge
                                              count={t("flights.bestPrice")}
                                              className="price-badge compact-badge hide-on-mobile"
                                            />
                                          ) : null}
                                          <Text className="price-amount compact-price" style={{ direction: "ltr", unicodeBidi: "plaintext" }}>
                                            {priceDisplay.usdMain}
                                          </Text>
                                          <div className="price-usd-inline">
                                            <Text className="price-usd compact-price-usd">
                                              {priceDisplay.ilsSecondary}
                                            </Text>
                                          </div>
                                        </div>
                                      </motion.div>
                                    </div>
                                    <motion.div
                                      className="motion-wrapper"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <Button
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        style={{
                                          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                          border: 'none',
                                          margin: '24px auto 0 auto',
                                          minWidth: '200px',
                                          display: 'flex',
                                          justifyContent: 'center'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          
                                          // Find the index of this deal in availableFlights
                                          const dealIndex = availableFlights?.findIndex(d => d === deal) ?? index;
                                          
                                          // Check if deal has linkedFlights as array
                                          let departLeg = null;
                                          let returnLeg = null;
                                          
                                          if (Array.isArray(deal.linkedFlights) && deal.linkedFlights.length > 0) {
                                            const firstLinkedFlight = deal.linkedFlights[0];
                                            if (firstLinkedFlight?.flight?.[0]?.leg && firstLinkedFlight?.flight?.[1]?.leg) {
                                              departLeg = firstLinkedFlight.flight[0].leg;
                                              returnLeg = firstLinkedFlight.flight[1].leg;
                                            }
                                          } else if (deal.linkedFlights?.flight) {
                                            // Check if deal has linkedFlights.flight directly
                                            const flightsArray = deal.linkedFlights.flight;
                                            if (Array.isArray(flightsArray) && flightsArray.length >= 2) {
                                              if (flightsArray[0]?.leg && flightsArray[1]?.leg) {
                                                departLeg = flightsArray[0].leg;
                                                returnLeg = flightsArray[1].leg;
                                              }
                                            }
                                          }
                                          
                                          if (departLeg && returnLeg) {
                                            // CRITICAL: Set manual selection flags FIRST before any updates
                                            // This prevents useEffect from overwriting the selection
                                            // Use ref FIRST to ensure it's set before any effects run
                                            isManualSelectionRef.current = true;
                                            setIsManualSelection(true);
                                            
                                            // CRITICAL: Save manual selection in BOTH state and ref
                                            const manualFlights = { depart: departLeg, return: returnLeg };
                                            manuallySelectedFlightsRef.current = manualFlights;
                                            manuallySelectedIndexRef.current = dealIndex;
                                            setManuallySelectedFlights(manualFlights);
                                            setManuallySelectedIndex(dealIndex);
                                            previousLinkedFlightsRef.current = linkedFlights;
                                            
                                            // Update flights IMMEDIATELY - do this BEFORE setSavedDeal
                                            // to ensure the UI updates immediately
                                            setSelectedDepart(departLeg);
                                            setSelectedReturn(returnLeg);
                                            setSelectedLinkedFlightIndex(dealIndex);
                                            
                                            // Update linkedFlights in context
                                            setSavedDeal(deal);
                                            
                                            // Get flight times for notification
                                            const departTime = departLeg?.depDate?.split(" ")[1] || "";
                                            const returnTime = returnLeg?.depDate?.split(" ")[1] || "";
                                            
                                            // Close drawer - use requestAnimationFrame for mobile
                                            if (isMobile) {
                                              requestAnimationFrame(() => {
                                                requestAnimationFrame(() => {
                                                  setIsSelectorOpen(false);
                                                  
                                                  // Show notification
                                                  try {
                                                    notification.success({
                                                      message: "✓ הטיסה התחלפה בהצלחה",
                                                      description: departTime && returnTime 
                                                        ? `טיסה הלוך: ${departTime} | טיסה חזור: ${returnTime}`
                                                        : "הטיסה נבחרה בהצלחה",
                                                      duration: 4,
                                                      placement: "topRight",
                                                      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                                      style: {
                                                        zIndex: 10000002,
                                                      },
                                                    });
                                                  } catch (error) {
                                                    console.error("Error showing notification:", error);
                                                  }
                                                });
                                              });
                                            } else {
                                              setIsSelectorOpen(false);
                                              try {
                                                notification.success({
                                                  message: "✓ הטיסה התחלפה בהצלחה",
                                                  description: departTime && returnTime 
                                                    ? `טיסה הלוך: ${departTime} | טיסה חזור: ${returnTime}`
                                                    : "הטיסה נבחרה בהצלחה",
                                                  duration: 4,
                                                  placement: "topRight",
                                                  icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                                  style: {
                                                    zIndex: 10000002,
                                                  },
                                                });
                                              } catch (error) {
                                                console.error("Error showing notification:", error);
                                              }
                                            }
                                          }
                                        }}
                                        className="choose-flight-btn compact-btn"
                                        size={isMobile ? "small" : "default"}
                                        block={isMobile}
                                      >
                                        {t("flights.chooseFlight")}
                                      </Button>
                                    </motion.div>
                                  </div>
                                </Card>
                              </motion.div>
                            );
                          })
                      : /* Show linkedFlights when there are multiple linked flights but no availableFlights */
                        Array.isArray(linkedFlights) && linkedFlights.length > 1
                        ? linkedFlights.map((linkedFlight, index) => {
                            return (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  duration: 0.3,
                                  delay: index * 0.1,
                                }}
                              >
                                <Card
                                  hoverable
                                  className={`flight-option-card compact-flight-card ${
                                    index === selectedLinkedFlightIndex
                                      ? "selected-flight-card"
                                      : ""
                                  }`}
                                  styles={{
                                    body: { padding: isMobile ? "8px" : "12px" },
                                  }}
                                >
                                  <div className="flight-option-content">
                                    {isCompact
                                      ? renderFlightsOnlyCompact(linkedFlight)
                                      : renderFlightsOnly(linkedFlight)}
                                  </div>

                                  <Divider className="tw-my-4" />

                                  <div className="flight-option-footer compact-footer">
                                    <motion.div
                                      className="motion-wrapper"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <Button
                                        type={
                                          index === selectedLinkedFlightIndex
                                            ? "primary"
                                            : "default"
                                        }
                                        icon={
                                          index ===
                                          selectedLinkedFlightIndex ? (
                                            <CheckCircleOutlined />
                                          ) : null
                                        }
                                        style={index === selectedLinkedFlightIndex ? {
                                          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                          border: 'none',
                                          margin: '24px auto 0 auto',
                                          minWidth: '200px',
                                          display: 'flex',
                                          justifyContent: 'center'
                                        } : {
                                          margin: '24px auto 0 auto',
                                          minWidth: '200px',
                                          display: 'flex',
                                          justifyContent: 'center'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          
                                          if (linkedFlight?.flight?.[0]?.leg && linkedFlight?.flight?.[1]?.leg) {
                                            const departLeg = linkedFlight.flight[0].leg;
                                            const returnLeg = linkedFlight.flight[1].leg;
                                            
                                      // CRITICAL: Save manual selection in BOTH state and ref for maximum reliability
                                      // State persists across re-renders, ref is for immediate checks
                                      const manualFlights = { depart: departLeg, return: returnLeg };
                                      
                                      // Save in STATE first (persists across re-renders)
                                      setManuallySelectedFlights(manualFlights);
                                      setManuallySelectedIndex(index);
                                      setIsManualSelection(true);
                                      
                                      // Also save in REF (for immediate checks)
                                      manuallySelectedFlightsRef.current = manualFlights;
                                      manuallySelectedIndexRef.current = index;
                                      isManualSelectionRef.current = true;
                                      previousLinkedFlightsRef.current = linkedFlights;
                                      
                                      // Update flights IMMEDIATELY
                                      setSelectedDepart(departLeg);
                                      setSelectedReturn(returnLeg);
                                      setSelectedLinkedFlightIndex(index);
                                      
                                      // Get flight times for notification
                                      const departTime = departLeg?.depDate?.split(" ")[1] || "";
                                      const returnTime = returnLeg?.depDate?.split(" ")[1] || "";
                                      
                                      // Close drawer - use requestAnimationFrame for mobile to ensure state is set
                                      if (isMobile) {
                                        requestAnimationFrame(() => {
                                          requestAnimationFrame(() => {
                                            setIsSelectorOpen(false);
                                            
                                            // Show notification
                                            try {
                                              notification.success({
                                                message: "✓ הטיסה התחלפה בהצלחה",
                                                description: departTime && returnTime 
                                                  ? `טיסה הלוך: ${departTime} | טיסה חזור: ${returnTime}`
                                                  : "הטיסה נבחרה בהצלחה",
                                                duration: 4,
                                                placement: "topRight",
                                                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                                style: {
                                                  zIndex: 10000002,
                                                },
                                              });
                                            } catch (error) {
                                              console.error("Error showing notification:", error);
                                            }
                                          });
                                        });
                                      } else {
                                        // Desktop - close immediately
                                        setIsSelectorOpen(false);
                                        try {
                                          notification.success({
                                            message: "✓ הטיסה התחלפה בהצלחה",
                                            description: departTime && returnTime 
                                              ? `טיסה הלוך: ${departTime} | טיסה חזור: ${returnTime}`
                                              : "הטיסה נבחרה בהצלחה",
                                            duration: 4,
                                            placement: "topRight",
                                            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                                            style: {
                                              zIndex: 10000002,
                                            },
                                          });
                                        } catch (error) {
                                          console.error("Error showing notification:", error);
                                        }
                                      }
                                          } else {
                                            console.error("FlightSelection - Missing flight data:", {
                                              hasFlight: !!linkedFlight?.flight,
                                              hasDepartLeg: !!linkedFlight?.flight?.[0]?.leg,
                                              hasReturnLeg: !!linkedFlight?.flight?.[1]?.leg,
                                            });
                                          }
                                        }}
                                        className="choose-flight-btn compact-btn"
                                        size={isMobile ? "small" : "default"}
                                      >
                                        {index === selectedLinkedFlightIndex
                                          ? t("flights.selected", "נבחר")
                                          : t("flights.chooseFlight")}
                                      </Button>
                                    </motion.div>
                                  </div>
                                </Card>
                              </motion.div>
                            );
                          })
                        : null}
          </div>
        </Drawer>
      </motion.div>
    );
  };

  // Helper function to compare flights more accurately
  const flightsMatch = (flight1, flight2) => {
    if (!flight1 || !flight2) return false;
    // Compare by depDate (most reliable)
    if (flight1.depDate !== flight2.depDate) return false;
    // Also compare by flightNumber if available
    if (flight1.flightNumber && flight2.flightNumber) {
      if (flight1.flightNumber !== flight2.flightNumber) return false;
    }
    // Also compare by airlineId if available
    if (flight1.airlineId && flight2.airlineId) {
      if (flight1.airlineId !== flight2.airlineId) return false;
    }
    return true;
  };

  // Only auto-update flights if they haven't been manually selected
  useEffect(() => {
    // If user manually selected flights, NEVER update them automatically
    if (isManualSelectionRef.current || isManualSelection) {
      return;
    }
    
    // Only update if current flights are null or empty
    if (!selectedDepart || !selectedReturn) {
      // Get the flights that should be selected based on the index
      let newDepart = null;
      let newReturn = null;
      
      // Try to get flights from linkedFlights first
      if (
        Array.isArray(linkedFlights) &&
        linkedFlights.length > 0 &&
        linkedFlights[selectedLinkedFlightIndex]?.flight?.[0]?.leg
      ) {
        const f = linkedFlights[selectedLinkedFlightIndex].flight;
        newDepart = f[0].leg;
        newReturn = f[1]?.leg ?? null;
      } else if (
        availableFlights &&
        Array.isArray(availableFlights) &&
        availableFlights.length > 0
      ) {
        // Fallback: Try to get flights from availableFlights (for dynamic packages)
        const selectedDeal = availableFlights[selectedLinkedFlightIndex] || availableFlights[0];
        
        if (Array.isArray(selectedDeal?.linkedFlights) && selectedDeal.linkedFlights.length > 0) {
          const firstLinkedFlight = selectedDeal.linkedFlights[0];
          if (firstLinkedFlight?.flight?.[0]?.leg) {
            newDepart = firstLinkedFlight.flight[0].leg;
            newReturn = firstLinkedFlight.flight[1]?.leg ?? null;
          }
        } else if (selectedDeal?.linkedFlights?.flight) {
          const flightsArray = selectedDeal.linkedFlights.flight;
          if (Array.isArray(flightsArray) && flightsArray.length >= 1) {
            if (flightsArray[0]?.leg) {
              newDepart = flightsArray[0].leg;
              newReturn = flightsArray[1]?.leg ?? null;
            }
          }
        }
      }
      
      if (newDepart && newReturn) {
        setSelectedDepart(newDepart);
        setSelectedReturn(newReturn);
      } else if (flightTripType === "OneWay" && newDepart) {
        setSelectedDepart(newDepart);
        setSelectedReturn(newReturn);
      }
    }
    // Only run when linkedFlights, availableFlights, or selectedLinkedFlightIndex change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    linkedFlights,
    availableFlights,
    selectedLinkedFlightIndex,
    flightTripType,
  ]);

  const renderFlightsOnly = (linkedFlights) => {
    if (linkedFlights === null) return null;

    if (Array.isArray(linkedFlights)) {
      // Don't render until selectedDepart and selectedReturn are set
      if (selectedDepart === null || selectedReturn === null) {
        return null;
      }

      // Filter flights based on the selected linked flight index
      const currentLinkedFlight =
        linkedFlights[selectedLinkedFlightIndex] || linkedFlights[0];
      const currentFlights = [currentLinkedFlight];

      const departFlights = currentFlights
        .map((linkedFlight, index) => {
          return linkedFlight.flight[0].leg;
        })
        .reduce((acc, curr) => {
          if (acc.findIndex((item) => item.depDate === curr.depDate) === -1) {
            acc.push(curr);
          }
          return acc;
        }, []);

      const returnFlights = currentFlights
        .map((linkedFlight, index) => {
          return linkedFlight.flight[1].leg;
        })
        .reduce((acc, curr) => {
          if (acc.findIndex((item) => item.depDate === curr.depDate) === -1) {
            acc.push(curr);
          }
          return acc;
        }, []);

      const departs = departFlights.map((leg, index) => {
        return {
          label: (
            <Space>
              <ClockCircleOutlined />
              <span>{leg.depDate.split(" ")[1]}</span>
            </Space>
          ),
          key: index.toString(),
          children: (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FlightFooterRow
                key={index}
                flight={leg}
                title={t("packages.firstWayFlight")}
              />
            </motion.div>
          ),
        };
      });

      const returns = returnFlights.map((leg, index) => {
        const isDisabled =
          !selectedDepart ||
          currentFlights.findIndex(
            (linkedFlight) =>
              linkedFlight.flight[1].leg.depDate === leg.depDate &&
              linkedFlight.flight[0].leg.depDate === selectedDepart.depDate
          ) === -1;

        return {
          label: (
            <Space>
              <ClockCircleOutlined />
              <span>{leg.depDate.split(" ")[1]}</span>
            </Space>
          ),
          key: index.toString(),
          children: (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FlightFooterRow
                key={index}
                flight={leg}
                title={t("packages.returnFlight")}
              />
            </motion.div>
          ),
          disabled: isDisabled,
        };
      });

      if (loadingLinkedFlights) {
        return (
          <motion.div
            className="skeleton-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Skeleton active paragraph={{ rows: 8 }} className="tw-w-full" />
            <Skeleton active paragraph={{ rows: 6 }} className="tw-w-full" style={{ marginTop: "20px" }} />
            <Skeleton active paragraph={{ rows: 4 }} className="tw-w-full" style={{ marginTop: "20px" }} />
          </motion.div>
        );
      }

      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flights-tabs-container"
        >
          {/* Flight Selection Dropdown - Only show if there are multiple flights */}
          {linkedFlights.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flight-selector-dropdown tw-mb-4"
            >
              <div className="tw-flex tw-items-center tw-gap-3">
                <Text strong className="tw-text-gray-700">
                  {t("flights.selectFlightOption", "בחר אפשרות טיסה:")}
                </Text>
                <Select
                  value={selectedLinkedFlightIndex}
                  onChange={(index) => {
                    // Mark as manual selection in both state and ref
                    setIsManualSelection(true);
                    isManualSelectionRef.current = true;
                    setManuallySelectedIndex(index);
                    manuallySelectedIndexRef.current = index;
                    setSelectedLinkedFlightIndex(index);
                    
                    // Call onFlightSelect callback if provided
                    if (onFlightSelect && Array.isArray(linkedFlights) && linkedFlights[index]) {
                      console.log('[FlightSelection] 🎯 Dropdown selection - calling onFlightSelect:', {
                        index,
                        selectedFlight: linkedFlights[index],
                        hasFlight: !!linkedFlights[index]?.flight,
                        flightLength: Array.isArray(linkedFlights[index]?.flight) ? linkedFlights[index].flight.length : linkedFlights[index]?.flight ? 1 : 0
                      });
                      onFlightSelect(linkedFlights[index], index); // Pass both flight and index
                    }
                  }}
                  className="tw-min-w-48"
                  size="large"
                >
                  {linkedFlights.map((linkedFlight, index) => {
                    const departLeg = linkedFlight.flight[0].leg;
                    const returnLeg = linkedFlight.flight[1].leg;
                    const departTime = departLeg.depDate
                      ? departLeg.depDate.split(" ")[1]
                      : "";
                    const returnTime = returnLeg.depDate
                      ? returnLeg.depDate.split(" ")[1]
                      : "";

                    return (
                      <Select.Option key={index} value={index}>
                        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                          <span>
                            {t("flights.option", "אפשרות")} {index + 1}
                          </span>
                          <span className="tw-text-xs tw-text-gray-500">
                            {departTime} → {returnTime}
                          </span>
                        </div>
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            </motion.div>
          )}

          {/* Departure Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flight-section departure-section"
          >
            <div className="section-header">
              <div className="section-icon">
                <ArrowLeftOutlined className="tw-text-blue-600" />
              </div>
              <Title level={4} className="section-title tw-mb-0">
                <span>טיסה הלוך</span>
              </Title>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Tabs
                key="departs"
                items={departs}
                activeKey={departFlights
                  .findIndex(
                    (depart) => depart.depDate === selectedDepart.depDate
                  )
                  .toString()}
                onChange={(key) => {
                  const depart = departFlights[Number(key)];
                  if (
                    !!selectedDepart &&
                    selectedDepart.depDate === depart.depDate
                  )
                    return;
                  const flightIndex = currentFlights.findIndex(
                    (linkedFlight) =>
                      linkedFlight.flight[0].leg.depDate === depart.depDate
                  );
                  const returnFlight =
                    currentFlights[flightIndex].flight[1].leg;
                  
                  // Mark as manual selection in both state and ref
                  const manualFlights = { depart: depart, return: returnFlight };
                  setManuallySelectedFlights(manualFlights);
                  setIsManualSelection(true);
                  manuallySelectedFlightsRef.current = manualFlights;
                  isManualSelectionRef.current = true;
                  previousLinkedFlightsRef.current = linkedFlights;
                  
                  // Update selectedLinkedFlightIndex BEFORE updating flights
                  if (flightIndex >= 0 && flightIndex !== selectedLinkedFlightIndex) {
                    // Save the manually selected index in both state and ref
                    setManuallySelectedIndex(flightIndex);
                    manuallySelectedIndexRef.current = flightIndex;
                    setSelectedLinkedFlightIndex(flightIndex);
                    
                    // Call onFlightSelect callback if provided
                    if (onFlightSelect && Array.isArray(linkedFlights) && linkedFlights[flightIndex]) {
                      console.log('[FlightSelection] 🎯 Depart tab selection - calling onFlightSelect:', {
                        flightIndex,
                        selectedFlight: linkedFlights[flightIndex],
                        hasFlight: !!linkedFlights[flightIndex]?.flight,
                        flightLength: Array.isArray(linkedFlights[flightIndex]?.flight) ? linkedFlights[flightIndex].flight.length : linkedFlights[flightIndex]?.flight ? 1 : 0
                      });
                      onFlightSelect(linkedFlights[flightIndex], flightIndex); // Pass both flight and index
                    }
                  }
                  
                  // Update flights
                  setSelectedDepart(depart);
                  setSelectedReturn(returnFlight);
                }}
                className="flight-tabs"
                size="large"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Divider className="section-divider" />
          </motion.div>

          {/* Return Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="flight-section return-section"
          >
            <div className="section-header">
              <div className="section-icon">
                {<ArrowRightOutlined className="tw-text-green-600" />}
              </div>
              <Title level={4} className="section-title tw-mb-0">
                <span>טיסה חזור</span>
              </Title>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Tabs
                key="returns"
                items={returns}
                activeKey={returnFlights
                  .findIndex(
                    (returnFlight) =>
                      returnFlight.depDate === selectedReturn.depDate
                  )
                  .toString()}
                onChange={(key) => {
                  const flight = returnFlights[Number(key)];
                  if (
                    !!selectedReturn &&
                    selectedReturn.depDate === flight.depDate
                  )
                    return;
                  
                  // Mark as manual selection in both state and ref
                  setIsManualSelection(true);
                  isManualSelectionRef.current = true;
                  previousLinkedFlightsRef.current = linkedFlights;
                  
                  // Update the manually selected return flight (keep existing depart)
                  const currentManual = manuallySelectedFlights.depart && manuallySelectedFlights.return
                    ? manuallySelectedFlights
                    : manuallySelectedFlightsRef.current;
                  
                  const manualFlights = currentManual.depart 
                    ? { depart: currentManual.depart, return: flight }
                    : { depart: selectedDepart, return: flight };
                  
                  setManuallySelectedFlights(manualFlights);
                  manuallySelectedFlightsRef.current = manualFlights;
                  
                  setSelectedReturn(flight);
                  
                  // Call onFlightSelect callback if provided
                  // Find the linked flight that matches the selected depart and return
                  if (onFlightSelect && Array.isArray(linkedFlights)) {
                    const matchingLinkedFlight = linkedFlights.find((lf) => {
                      const lfDepart = lf.flight[0]?.leg;
                      const lfReturn = lf.flight[1]?.leg;
                      return lfDepart?.depDate === (manualFlights.depart?.depDate || selectedDepart?.depDate) &&
                             lfReturn?.depDate === flight.depDate;
                    });
                    console.log('[FlightSelection] 🎯 Return tab selection - finding matching flight:', {
                      manualFlights,
                      selectedDepart,
                      returnFlight: flight,
                      matchingLinkedFlight,
                      hasMatchingFlight: !!matchingLinkedFlight,
                      allLinkedFlights: linkedFlights.map((lf, idx) => ({
                        index: idx,
                        depart: lf.flight[0]?.leg?.depDate,
                        return: lf.flight[1]?.leg?.depDate
                      }))
                    });
                    if (matchingLinkedFlight) {
                      // Find the index of the matching flight
                      const matchingIndex = linkedFlights.findIndex((lf) => lf === matchingLinkedFlight);
                      console.log('[FlightSelection] ✅ Found matching flight, calling onFlightSelect:', {
                        matchingIndex,
                        matchingFlight: matchingLinkedFlight
                      });
                      onFlightSelect(matchingLinkedFlight, matchingIndex >= 0 ? matchingIndex : undefined); // Pass both flight and index
                    } else {
                      console.warn('[FlightSelection] ⚠️ No matching flight found for selected depart and return');
                    }
                  }
                }}
                className="flight-tabs"
                size="large"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      );
    } else {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <FlightsFooter flights={linkedFlights?.flight} />
        </motion.div>
      );
    }
  };

  const renderFlightsOnlyCompact = (linkedFlights) => {
    // Handle different data structures for linked flights
    let flightsToRender = null;

    if (linkedFlights) {
      if (Array.isArray(linkedFlights)) {
        // Array of linked flights - use selected index or first one for compact display
        const selectedFlight =
          linkedFlights[selectedLinkedFlightIndex] || linkedFlights[0];
        flightsToRender = selectedFlight?.flight || linkedFlights;
      } else if (linkedFlights.flight) {
        // Single flight object with flight property
        flightsToRender = linkedFlights.flight;
      } else {
        // Single flight object
        flightsToRender = [linkedFlights];
      }
    }

    if (
      !flightsToRender ||
      (Array.isArray(flightsToRender) && flightsToRender.length === 0)
    ) {
      return (
        <div className="compact-flights-empty">
          <Text type="secondary">{t("flights.noFlightsAvailable")}</Text>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="compact-flights-container"
      >
        {/* Compact Flight Selection Dropdown - Only show if there are multiple flights */}
        {/* {Array.isArray(linkedFlights) && linkedFlights.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="compact-flight-selector tw-mb-3"
          >
            <Select
              value={selectedLinkedFlightIndex}
              onChange={setSelectedLinkedFlightIndex}
              className="tw-w-full"
              size="small"
              placeholder={t("flights.selectFlightOption", "בחר אפשרות טיסה")}
            >
              {linkedFlights.map((linkedFlight, index) => {
                const departLeg = linkedFlight.flight[0].leg;
                const returnLeg = linkedFlight.flight[1].leg;
                const departTime = departLeg.depDate
                  ? departLeg.depDate.split(" ")[1]
                  : "";
                const returnTime = returnLeg.depDate
                  ? returnLeg.depDate.split(" ")[1]
                  : "";

                return (
                  <Select.Option key={index} value={index}>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span className="tw-text-xs">
                        {t("flights.option", "אפשרות")} {index + 1}
                      </span>
                      <span className="tw-text-xs tw-text-gray-500">
                        {departTime} → {returnTime}
                      </span>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </motion.div>
        )} */}

        <div className="compact-flights-list compact-flights-list--unified-itinerary">
          {(() => {
            const rows = flattenPackageCompactFlightRows(flightsToRender);
            if (rows.length === 0) {
              return (
                <div className="compact-flights-empty compact-flights-empty--inline">
                  <Text type="secondary">{t("flights.noFlightsAvailable")}</Text>
                </div>
              );
            }
            const routeHeading = packageProfileRouteHeading(rows[0]?.leg);
            return (
              <div className="fpc fpc--package-itinerary-only" dir="rtl">
                <div className="fpc__zones">
                  <div className="fpc__zone fpc__zone--flights">
                    {routeHeading ? (
                      <p className="fpc__route-heading" dir="ltr">
                        {routeHeading}
                      </p>
                    ) : null}
                    <div className="fpc__itinerary">
                      <div className="fpc__legs-stack">
                        {rows.map((r) => (
                          <PackageFlightLegUnified
                            key={r.key}
                            leg={r.leg}
                            labelHe={r.labelHe}
                            showDivider={r.showDivider}
                            legIndex={r.legIndex}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </motion.div>
    );
  };

  const flightSelectionCollapsed = [
    {
      key: "1",
      label: "טיסות",
      children: renderFlights(),
    },
  ];

  if (isCollaped || isCard)
    return (
      <ConfigProvider
        theme={{
          components: {
            Collapse: {
              headerBg: "#0258a4",
              colorTextHeading: "#ffffff",
            },
          },
        }}
      >
        {isCollaped ? (
          <Collapse
            style={{
              width: "100%",
            }}
            items={flightSelectionCollapsed}
            dir="rtl"
            defaultActiveKey={["1"]}
          />
        ) : (
          <Card
            title={title}
            style={{
              width: "100%",
              borderRadius: "10px",
            }}
            styles={{
              body: {
                padding: "10px",
                display: "flex",
                flexDirection: "row",
                width: "100%",
                gap: "10px",
                flexWrap: "wrap",
              },
              header: {
                padding: "10px",
                fontSize: "1rem",
                fontWeight: "bold",
                color: "#0258a4",
                minHeight: "unset",
              },
              title: {
                textOverflow: "unset",
                whiteSpace: "unset",
              },
            }}
          >
            {renderFlights()}
          </Card>
        )}
      </ConfigProvider>
    );

  return renderFlights();
};

export default FlightSelection;
